/**
 * Recombee Feed Component
 * Displays personalized product recommendations with batch loading
 * - Fetches 100 products at once
 * - Shows 24 at a time via infinite scroll
 * - Load more button at end of batch to fetch next 100
 * 
 * @typedef {Object} RecombeeValues
 * @property {string} [handle]
 * @property {string} [title]
 * @property {number} [price]
 * @property {number} [compareAtPrice]
 * @property {number} [compare_at_price]
 * @property {string} [imageUrl]
 * @property {string} [image_url]
 * @property {string} [imageUrl2]
 * @property {string} [image_url_2]
 * @property {boolean} [available]
 * @property {string} [url]
 * @property {string} [vendor]
 * @property {number} [inventory_quantity] - Total stock quantity
 * @property {number} [sold_count] - Recent sales count
 * @property {string[]} [sizes_available] - Available sizes array
 * @property {Object.<string, number>} [variants_inventory] - Inventory per variant/size
 * 
 * @typedef {{ id: string, values?: RecombeeValues }} RecombeeRecommendation
 * 
 * @typedef {Object} RecombeeResponse
 * @property {RecombeeRecommendation[]} [recommendations]
 * @property {string} [recommId]
 * @property {boolean} [hasMore]
 * @property {boolean} [success]
 * @property {string} [error]
 */

const RECOMBEE_API_BASE = '/apps/alux-latest/api/recombee';
// Direct Vercel URL for global cached requests (bypasses Shopify App Proxy)
// This enables CDN caching since all users hit the same edge
const RECOMBEE_DIRECT_URL = 'https://alux-official-app.vercel.app/api/recombee';
const BATCH_SIZE = 100;  // Fetch 100 products at once
const DISPLAY_SIZE = 24; // Show 24 at a time

// FOMO Configuration
const FOMO_CONFIG = {
  FAST_SELLING_THRESHOLD: 10,    // Sold X+ in last 24h = "Vente rapide"
  LOW_STOCK_THRESHOLD: 5,        // Stock <= X = "Presque rupture"
  SHOW_SIZES_THRESHOLD: 10       // Stock <= X shows available sizes
};

/**
 * Generate FOMO badge HTML and sizes text based on product data
 * @param {RecombeeValues} values - Product values from Recombee
 * @param {boolean} available - Product availability
 * @returns {{badgeHtml: string, sizesText: string, isLowStock: boolean}} Badge HTML and sizes text
 */
function getFomoBadge(values, available) {
  if (!available) {
    return { badgeHtml: '', sizesText: '', isLowStock: false };
  }

  const inventory = values.inventory_quantity || 0;
  // sizes_available is a set from Recombee (comes as array)
  // Filter out "Default Title" (any casing) which Shopify uses for products without size variants
  const sizesAvailable = Array.isArray(values.sizes_available) 
    ? values.sizes_available.filter(s => s && typeof s === 'string' && s.toLowerCase() !== 'default title')
    : [];
  // variants_inventory is stored as JSON string in Recombee
  let variantsInventory = {};
  if (values.variants_inventory) {
    try {
      variantsInventory = typeof values.variants_inventory === 'string' 
        ? JSON.parse(values.variants_inventory) 
        : values.variants_inventory;
    } catch (e) {
      console.warn('[FOMO] Failed to parse variants_inventory:', e);
    }
  }

  // Get sizes text for display under title - only show when < 4 sizes
  let sizesText = '';
  if (sizesAvailable.length > 0 && sizesAvailable.length < 4) {
    sizesText = `Dernières tailles : ${sizesAvailable.join(', ')}`;
  }
  // 4+ sizes = nothing displayed

  // Priority 1: Presque rupture (blood red) - Stock critique
  if (inventory > 0 && inventory <= FOMO_CONFIG.LOW_STOCK_THRESHOLD) {
    return {
      badgeHtml: `
        <div class="fomo-badge fomo-badge--presque-rupture">
          <svg class="fomo-badge__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
          <span class="fomo-badge__text">Presque rupture</span>
        </div>
      `,
      sizesText: sizesText, // Show sizes text for all products
      isLowStock: true
    };
  }

  // Normal stock - just return sizes text for display
  return { 
    badgeHtml: '', 
    sizesText: sizesText,
    isLowStock: false 
  };
}

/**
 * Simple LRU Cache for Recombee API responses
 * Now with sessionStorage persistence for cross-page caching
 */
class LRUCache {
  #maxSize;
  #ttl;
  #cache = new Map();
  #storageKey = 'recombee_cache';

  /**
   * @param {number} maxSize - Maximum number of entries
   * @param {number} ttl - Time to live in ms
   */
  constructor(maxSize = 10, ttl = 5 * 60 * 1000) {
    this.#maxSize = maxSize;
    this.#ttl = ttl;
    this.#loadFromStorage();
  }

  /** Load cache from sessionStorage */
  #loadFromStorage() {
    try {
      const stored = sessionStorage.getItem(this.#storageKey);
      if (stored) {
        const entries = JSON.parse(stored);
        const now = Date.now();
        // Only restore non-expired entries
        for (const [key, entry] of entries) {
          if (entry.expires > now) {
            this.#cache.set(key, entry);
          }
        }
        console.log('[Recombee LRU] Loaded from sessionStorage:', this.#cache.size, 'entries');
      }
    } catch (e) {
      console.warn('[Recombee LRU] Failed to load from sessionStorage:', e);
    }
  }

  /** Save cache to sessionStorage */
  #saveToStorage() {
    try {
      const entries = Array.from(this.#cache.entries());
      sessionStorage.setItem(this.#storageKey, JSON.stringify(entries));
    } catch (e) {
      console.warn('[Recombee LRU] Failed to save to sessionStorage:', e);
    }
  }

  /**
   * @param {string} key
   * @returns {any|null}
   */
  get(key) {
    const entry = this.#cache.get(key);
    if (!entry) {
      console.log('[Recombee LRU] Cache MISS for key:', key);
      return null;
    }
    
    // Check TTL
    if (Date.now() > entry.expires) {
      console.log('[Recombee LRU] Cache EXPIRED for key:', key);
      this.#cache.delete(key);
      this.#saveToStorage();
      return null;
    }
    
    // Move to end (most recently used)
    this.#cache.delete(key);
    this.#cache.set(key, entry);
    console.log('[Recombee LRU] Cache HIT for key:', key, 'TTL remaining:', Math.round((entry.expires - Date.now()) / 1000), 's');
    return entry.data;
  }

  /**
   * @param {string} key
   * @param {any} data
   */
  set(key, data) {
    // Remove if exists (to update position)
    this.#cache.delete(key);
    
    // Evict oldest if at capacity
    if (this.#cache.size >= this.#maxSize) {
      const oldest = this.#cache.keys().next().value;
      this.#cache.delete(oldest);
      console.log('[Recombee LRU] Evicted oldest entry:', oldest);
    }
    
    this.#cache.set(key, {
      data,
      expires: Date.now() + this.#ttl
    });
    console.log('[Recombee LRU] Cached response for key:', key, 'TTL:', this.#ttl / 1000, 's');
    this.#saveToStorage();
  }

  /**
   * Create a unique cache key
   * For 'product' type, key is GLOBAL (no userId) - same cache for all users
   * For other types, key includes userId for personalized caching
   * @param {string} type - Recommendation type (feed, product, cart, homepage)
   * @param {string} userId - User identifier (ignored for 'product' type)
   * @param {string|null} cursor - Pagination cursor
   * @param {string|null} itemId - Product ID for product-specific recommendations
   * @param {string|null} cartItems - Cart items for cart recommendations
   * @returns {string}
   */
  static createKey(type, userId, cursor, itemId = null, cartItems = null) {
    // Product recommendations are GLOBAL - don't include userId
    const parts = type === 'product' ? [type] : [type, userId];
    if (itemId) parts.push(`item:${itemId}`);
    if (cartItems) parts.push(`cart:${cartItems}`);
    parts.push(cursor || 'init');
    return parts.join(':');
  }
}

// Global cache instance (persists across component instances)
const recombeeCache = new LRUCache(10, 5 * 60 * 1000);

/**
 * Escape HTML to prevent XSS attacks
 * @param {string} str
 * @returns {string}
 */
const escapeHtml = (str) => {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

class RecombeeFeed extends HTMLElement {
  /** @type {boolean} */
  #loading = false;
  /** @type {boolean} */
  #hasMoreFromAPI = true;
  /** @type {string|null} */
  #userId = null;
  /** @type {string|null} */
  #lastRecommId = null;
  /** @type {Set<string>} */
  #loadedProductIds = new Set();
  /** @type {IntersectionObserver|null} */
  #observer = null;
  /** @type {AbortController|null} */
  #abortController = null;
  /** @type {RecombeeRecommendation[]} */
  #buffer = [];

  connectedCallback() {
    this.#userId = this.#getOrCreateUserId();
    this.#setupInfiniteScroll();
    this.#setupLoadMoreButton();
    
    if (this.dataset.autoload !== 'false') {
      this.#fetchBatch();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#abortController?.abort();
  }

  /** @returns {string} */
  #getOrCreateUserId() {
    // 1. Logged-in customer
    if (window.Shopify?.customer?.id) {
      const customerId = `customer-${window.Shopify.customer.id}`;
      console.log('[Recombee] Using customer ID:', customerId);
      return customerId;
    }
    // 2. Shopify's 1-year cookie (_shopify_y)
    const shopifyCookie = this.#getCookie('_shopify_y');
    console.log('[Recombee] Using Shopify cookie ID:', shopifyCookie);
    return shopifyCookie || 'anonymous';
  }

  /**
   * @param {string} name
   * @returns {string | null}
   */
  #getCookie(name) {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match?.[2] ?? null;
  }

  #setupInfiniteScroll() {
    const sentinel = this.querySelector('[data-load-more-sentinel]');
    if (!sentinel) return;
    
    this.#observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !this.#loading) {
        this.#showNextBatch();
      }
    }, { rootMargin: '400px' });
    
    this.#observer.observe(sentinel);
  }

  #setupLoadMoreButton() {
    const btn = this.querySelector('[data-load-more-btn]');
    if (btn) {
      btn.addEventListener('click', () => this.#fetchBatch());
    }
  }

  /**
   * Fetch a batch of 100 products from API
   */
  async #fetchBatch() {
    if (this.#loading || !this.#hasMoreFromAPI) return;
    this.#loading = true;

    const feedType = this.dataset.type || 'feed';
    const itemId = this.dataset.productId || null;
    const cartItems = this.dataset.cartItems || null;
    const cacheKey = LRUCache.createKey(
      feedType, 
      this.#userId || 'anon', 
      this.#lastRecommId,
      itemId,
      cartItems
    );

    // Check cache first
    const cached = recombeeCache.get(cacheKey);
    if (cached) {
      console.log('[Recombee] Cache hit for', feedType, itemId ? `product:${itemId}` : '');
      this.#processCachedData(cached);
      this.#loading = false;
      return;
    }

    console.log('[Recombee] Cache miss, fetching...', feedType, itemId ? `product:${itemId}` : '');
    this.#showLoader();
    this.#hideLoadMoreButton();

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const params = new URLSearchParams({
        type: feedType,
        count: String(BATCH_SIZE),
        return_properties: 'true'
      });
      
      // For product recommendations, don't send user_id (global cache)
      // For other types, include user_id for personalization
      if (feedType !== 'product') {
        params.set('user_id', this.#userId || 'anonymous');
      }
      
      if (this.#lastRecommId) params.set('cursor', this.#lastRecommId);
      if (this.dataset.productId) params.set('item_id', this.dataset.productId);
      if (this.dataset.cartItems) params.set('cart_items', this.dataset.cartItems);

      // For GLOBAL cached types (product), call Vercel directly to leverage CDN
      // For personalized types, use App Proxy (required for user tracking)
      const isGlobalCache = feedType === 'product';
      const apiBaseUrl = isGlobalCache ? RECOMBEE_DIRECT_URL : RECOMBEE_API_BASE;
      
      console.log('[Recombee] Request URL:', `${apiBaseUrl}/recommendations?${params}`, isGlobalCache ? '(DIRECT CDN)' : '(via App Proxy)');
      
      const response = await fetch(`${apiBaseUrl}/recommendations?${params}`, {
        signal: this.#abortController.signal
      });
      
      // Debug: Log Vercel CDN cache status
      const cacheStatus = {
        status: response.status,
        vercelCache: response.headers.get('X-Vercel-Cache'), // HIT, MISS, STALE, BYPASS
        age: response.headers.get('Age'), // Seconds since response was cached
        generatedAt: response.headers.get('X-Cache-Generated-At'),
        cacheType: response.headers.get('X-Cache-Type'),
        maxAge: response.headers.get('X-Cache-Max-Age'),
      };
      console.log('[Recombee Cache]', cacheStatus.vercelCache || 'NO-CDN', cacheStatus);
      
      console.log('[Recombee] Response status:', response.status);
      
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      console.log('[Recombee] Data received:', data);
      
      if (data.error) throw new Error(data.error);

      // Cache the response
      recombeeCache.set(cacheKey, data);

      this.#processCachedData(data);

    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      console.error('Recombee feed error:', error);
      this.#showError();
    } finally {
      this.#loading = false;
      this.#hideLoader();
    }
  }

  /**
   * Process data from API or cache
   * @param {RecombeeResponse} data
   */
  #processCachedData(data) {
    this.#lastRecommId = data.recommId ?? null;
    this.#hasMoreFromAPI = data.hasMore ?? false;

    const newRecs = (data.recommendations || []).filter(
      (/** @type {RecombeeRecommendation} */ r) => !this.#loadedProductIds.has(r.id)
    );
    
    console.log('[Recombee] New recs:', newRecs.length);
    this.#buffer.push(...newRecs);
    this.#showNextBatch();
  }

  /**
   * Show next 24 products from buffer
   */
  #showNextBatch() {
    if (this.#buffer.length === 0) {
      // Buffer empty - show load more button if API has more
      if (this.#hasMoreFromAPI) {
        this.#showLoadMoreButton();
      }
      return;
    }

    // Take next batch from buffer
    const batch = this.#buffer.splice(0, DISPLAY_SIZE);
    
    if (batch.length > 0) {
      this.#renderProducts(batch);
    }

    // If buffer is now empty and API has more, show load more button
    if (this.#buffer.length === 0 && this.#hasMoreFromAPI) {
      this.#showLoadMoreButton();
    }
  }

  #showLoader() {
    /** @type {HTMLElement|null} */
    const loader = this.querySelector('[data-recombee-loader]');
    if (loader) loader.hidden = false;
  }

  #hideLoader() {
    /** @type {HTMLElement|null} */
    const loader = this.querySelector('[data-recombee-loader]');
    if (loader) loader.hidden = true;
  }

  #showLoadMoreButton() {
    /** @type {HTMLElement|null} */
    const container = this.querySelector('[data-load-more-container]');
    if (container) container.hidden = false;
  }

  #hideLoadMoreButton() {
    /** @type {HTMLElement|null} */
    const container = this.querySelector('[data-load-more-container]');
    if (container) container.hidden = true;
  }

  #removeSkeletons() {
    const grid = this.querySelector('[data-recombee-grid]');
    if (!grid) return;
    grid.querySelectorAll('[data-skeleton]').forEach(s => s.remove());
  }

  #showError() {
    const grid = this.querySelector('[data-recombee-grid]');
    if (!grid) return;
    
    this.#removeSkeletons();
    
    if (grid.children.length === 0) {
      const errorEl = document.createElement('li');
      errorEl.className = 'recombee-error';
      errorEl.setAttribute('role', 'alert');
      errorEl.innerHTML = `
        <div style="text-align: center; padding: 2rem; color: var(--color-foreground); opacity: 0.6;">
          <p>Impossible de charger les recommandations</p>
          <button type="button" onclick="this.closest('recombee-feed')?.refresh()" 
            style="margin-top: 1rem; padding: 0.5rem 1rem; cursor: pointer; border: 1px solid currentColor; background: transparent; border-radius: 4px;">
            Réessayer
          </button>
        </div>
      `;
      grid.appendChild(errorEl);
    }
    
    this.dispatchEvent(new CustomEvent('recombee:error', { bubbles: true }));
  }

  /**
   * Render products directly from Recombee data
   * @param {RecombeeRecommendation[]} recommendations
   */
  #renderProducts(recommendations) {
    const grid = this.querySelector('[data-recombee-grid]');
    if (!grid) return;

    // Remove skeletons on first render
    this.#removeSkeletons();

    const fragment = document.createDocumentFragment();
    
    recommendations.forEach(rec => {
      const v = rec.values || {};
      const handle = escapeHtml(v.handle || rec.id);
      const title = escapeHtml(v.title || 'Product');
      const price = v.price ? this.#formatPrice(v.price) : '';
      const comparePrice = v.compareAtPrice || v.compare_at_price || 0;
      const imageUrl = v.imageUrl || v.image_url || '';
      const available = v.available !== false;
      const productUrl = v.url || `/products/${handle}`;
      
      const isOnSale = comparePrice > (v.price || 0) && comparePrice > 0;
      const isSoldOut = !available;
      
      this.#loadedProductIds.add(rec.id);
      
      const li = document.createElement('li');
      li.className = 'product-grid__item recombee-product-item';
      
      // Generate FOMO badge and sizes text
      const fomoBadge = getFomoBadge(v, available);
      
      // Only FOMO badges - sold out products are filtered by Recombee and never shown
      const badgeHtml = fomoBadge.badgeHtml;
      
      // Sizes text (shown under price for non-low-stock products)
      const sizesTextHtml = fomoBadge.sizesText 
        ? `<p class="product-card__sizes" style="margin: 0; font-size: 0.75rem; color: #6B6B6B; line-height: 1.2;">${fomoBadge.sizesText}</p>`
        : '';
      
      li.innerHTML = `
        <div class="product-card recombee-card" data-product-id="${rec.id}" data-recombee-item-id="${rec.id}" data-recombee-recomm-id="${this.#lastRecommId || ''}">
          <a href="${productUrl}" class="product-card__link">
            <span class="visually-hidden">${title}</span>
          </a>
          <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style" style="--product-card-gap: 8px;">
            <div class="card-gallery spacing-style border-style" style="--gallery-aspect-ratio: 1;">
              ${badgeHtml}
              ${imageUrl ? `
                <a href="${productUrl}" class="contents">
                  <img src="${imageUrl}" alt="${title}" class="product-media" loading="lazy" style="width: 100%; height: 100%; object-fit: cover;">
                </a>
              ` : `
                <a href="${productUrl}" class="contents">
                  <div style="width: 100%; padding-top: 100%; background: var(--color-background-secondary, #f5f5f5);"></div>
                </a>
              `}
            </div>
            <a class="contents user-select-text" href="${productUrl}">
              <p class="product-card__title paragraph" style="margin: 0; display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 1; overflow: hidden; text-overflow: ellipsis; line-height: 1.3;">
                ${title}
              </p>
            </a>
            ${sizesTextHtml}
            <div class="price">
              ${isSoldOut ? `
                <span class="price__sold-out">Épuisé</span>
              ` : isOnSale ? `
                <span class="price__sale">
                  <span class="price-item price-item--sale" style="font-weight: bold;">${price}</span>
                  <s class="price-item price-item--compare">${this.#formatPrice(comparePrice)}</s>
                </span>
              ` : `
                <span class="price__regular">
                  <span class="price-item" style="font-weight: bold;">${price}</span>
                </span>
              `}
            </div>
          </div>
        </div>
      `;
      
      fragment.appendChild(li);
    });

    grid.appendChild(fragment);

    this.dispatchEvent(new CustomEvent('recombee:products-loaded', {
      bubbles: true,
      detail: { count: recommendations.length, bufferRemaining: this.#buffer.length }
    }));
  }

  /**
   * Format price for display
   * @param {number} price - Price in DA (already converted during sync)
   * @returns {string}
   */
  #formatPrice(price) {
    if (!price && price !== 0) return '';
    // Price is already in DA from Recombee sync
    return Math.round(price) + ' DA';
  }

  /**
   * Update cart items and refresh
   * @param {string[]} cartItems
   */
  updateCartItems(cartItems) {
    this.dataset.cartItems = cartItems.join(',');
    this.refresh();
  }

  refresh() {
    this.#abortController?.abort();
    this.#loadedProductIds.clear();
    this.#buffer = [];
    this.#hasMoreFromAPI = true;
    this.#lastRecommId = null;
    this.#loading = false;
    
    const grid = this.querySelector('[data-recombee-grid]');
    if (grid) {
      grid.innerHTML = Array(DISPLAY_SIZE).fill(`
        <li class="product-grid__item recombee-skeleton" data-skeleton>
          <div class="product-card">
            <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style" style="--product-card-gap: 8px;">
              <div class="card-gallery spacing-style border-style skeleton-shimmer" style="--gallery-aspect-ratio: 1;"></div>
              <div class="skeleton-text skeleton-shimmer" style="width: 50%;"></div>
              <div class="skeleton-text skeleton-text--title skeleton-shimmer" style="width: 80%;"></div>
            </div>
          </div>
        </li>
      `).join('');
    }
    
    this.#hideLoadMoreButton();
    this.#fetchBatch();
  }
}

if (!customElements.get('recombee-feed')) {
  customElements.define('recombee-feed', RecombeeFeed);
}
